import { InvoicePaymentFilter, toPaidFlag } from '../models/invoice-payment-status';
import {
    Client,
    ClientSelectItem,
    EditInvoice,
    Filter,
    HandoverProtocol,
    Invoice,
    InvoiceData,
    InvoiceFilter,
    InvoicePaymentRequest,
    InvoicePayments,
    CreditNoteOffset,
    InvoiceProtocol,
    InvoiceProtocolPick,
    InvoiceProtocols,
    MergeableSibling,
    MonthlyInvoiceGenerateResult,
    MonthlyInvoiceRow,
    Option,
    TableData,
    User,
} from '../models/table-models';
import {
    TableRequestParams,
    transformInvoiceResponse,
    transformNoPagerResponse,
    transformTableResponse,
} from './data-hooks';
import interceptor from '../restServiceInterceptor';
import {
    Branch,
    BranchListItem,
    BranchPaymentInfo,
    BranchPieChartData,
    CreateEntitieResponse,
    DevicePieChartData,
    DistributorBranchChartData,
    DistributorDeviceChartData,
    DocumentView, InvoicelessDocumentView,
    InvoicePreview,
    MapBranches,
    MaterialBranch,
    NewRequestBranch,
    NewRequestBranches,
    ShortClientBranches,
    WarningsResult,
} from '../models/dashboard-models';
import { BranchDevice, Device } from '../models/warehouse-models';
import { resourceCache } from 'use-async-resource';
import { Turnover } from '../models/user-models';
import { CashOrder, CashOrderSummary } from '../models/cash-order-models';
import { InvoiceCorrectionInput } from '../models/invoice-corrections';

export const useGetClients = (props: TableRequestParams): Promise<TableData<Client[]>> => {
    let search = props.filter?.search ? props.filter.search : null;
    return interceptor.get('/client-service/clients', {
        params: {
            page: props['pager.page'] || undefined,
            itemsPerPage: props['pager.itemsPerPage'] || undefined,
            search,
        },
        transformResponse: transformTableResponse,
    });
};

export const isDepositCollectionAllowed = (branchId: number): Promise<boolean> =>
    interceptor.get('/client-service/api/v1/device-deposits/collection-allowed', { params: { branchId } });

export const useGetCashOrders = (clientId?: number, branchId?: number): Promise<CashOrderSummary[]> => {
    return interceptor.get('/client-service/api/v1/device-deposits/cash-orders', {
        params: { clientId, branchId },
    });
};

export const useGetCashOrder = (id: number): Promise<CashOrder> => {
    return interceptor.get(`/client-service/api/v1/device-deposits/cash-orders/${id}`);
};

export const getCashOrderPdf = (id: number): Promise<Blob> => {
    return interceptor.get(`/client-service/api/v1/device-deposits/cash-orders/${id}/pdf`, {
        responseType: 'blob',
        transformResponse: (data) => new Blob([data], { type: 'application/pdf' }),
    });
};


export const useUpdateClients = (updatedItem: Client) => {
    return interceptor.post('/client-service/clients', {
        ...updatedItem,
    });
};

export const useGetClient = (id: string): Promise<Client> => {
    return interceptor.get(`/client-service/clients/${id}`, {
        transformResponse: transformNoPagerResponse,
    });
};
export const useGetClientByCompanyName = (companyName: string): Promise<Client> => {
    return interceptor.get(`/client-service/clients/name`, {
        params: { companyName },
        transformResponse: transformNoPagerResponse,
    });
};
export const saveClient = (fields: Client): Promise<CreateEntitieResponse> => {
    return interceptor.post('/client-service/clients', {
        ...fields,
    });
};

export const editClient = (fields: Client, id: number | undefined): Promise<CreateEntitieResponse> => {
    return interceptor.post(`/client-service/clients`, {
        id,
        ...fields,
    });
};

export const checkClientWarnings = (payload: Client): Promise<WarningsResult> => {
    return interceptor.post('/client-service/clients/check', payload);
};

export interface CompanyLookupResult {
    found: boolean;
    companyName?: string | null;
    city?: string | null;
    address?: string | null;
    representative?: string | null;
    representativeRole?: string | null;
    vat?: string | null;
    vatStatus?: 'VALID' | 'INVALID' | 'UNAVAILABLE' | null;
    vatRegistered?: boolean | null;
    warnings: string[];
}

export const getCompanyLookup = (eik: string, fresh = false): Promise<CompanyLookupResult> =>
    interceptor.get('/client-service/clients/company-lookup', { params: { eik, fresh } });

export interface VatLookupResult {
    vat?: string | null;
    vatStatus?: 'VALID' | 'INVALID' | 'UNAVAILABLE' | null;
    vatRegistered?: boolean | null;
    /** Само при физическо лице: попълнено, когато 9-те цифри са ЕИК на фирма от Търговския регистър. */
    companyName?: string | null;
}

/** Регистрация по ДДС на физическо лице по ЕГН/ЛНЧ/БУЛСТАТ (VIES). */
export const getPersonVatLookup = (id: string): Promise<VatLookupResult> =>
    interceptor.get('/client-service/clients/person-vat-lookup', { params: { id } });

/** Регистрация по ДДС на фирма по ЕИК (VIES); при 13-цифрен ЕИК - по първите 9 цифри. */
export const getCompanyVatLookup = (eik: string): Promise<VatLookupResult> =>
    interceptor.get('/client-service/clients/company-vat-lookup', { params: { eik } });

export const setBranchInactive = (id: number) => {
    return interceptor.put(`/client-service/clients/branches/inactive/${id}`);
};

export const setBranchActive = (id: number) => {
    return interceptor.put(`/client-service/clients/branches/active/${id}`);
};

export const setDeviceInactive = (id: number | undefined, date: string) => {
    return interceptor.put(`/client-service/clients/devices/inactive/${id}?endDate=${date}`);
};

export const setDeviceActive = (id: number | undefined) => {
    return interceptor.put(`/client-service/clients/devices/active/${id}`);
};

export const useGetClientBranches = (): Promise<ShortClientBranches[]> => {
    return interceptor.get('/client-service/clients/short');
};

export const useGetBranchesByClient = ({ id, filter }: { id: number; filter?: {} }): Promise<MapBranches> => {
    let search = filter ? { ...filter } : null;
    
    return interceptor.get(`/client-service/clients/branches?clientId=${id}`, {
        params: {
            ...search,
        },
        transformResponse: transformTableResponse,
    });
};
export const useGetBranchById = (id: number): Promise<Branch> => {
    return interceptor.get('/client-service/clients/branches/' + id);
};

export const useGetBranchByIdInRequestView = (id: number): Promise<NewRequestBranch> => {
    return interceptor.get('/client-service/clients/branches/byView/' + id + "?view=REQUESTVIEW");
};

export const useGetNewRequestBranchesByClient = (id: number | undefined): Promise<NewRequestBranches> => {
   
    return interceptor.get(`/client-service/clients/branches?clientId=${id}&view=REQUESTVIEW`, {
        transformResponse:(response: string): TableData<[]> => {
            const data = response && JSON.parse(response);
        //   console.log('Data Client Branches ', data);
            return {
                items: data.items ?? [],
                pager: data.pager ?? {},
            };
        }
        // transformTableResponse,
    });
};

export const UseNewRequestAddBranch = (
    field: NewRequestBranch,
    clientId: number | undefined
): Promise<CreateEntitieResponse> => {
    let formData = new FormData();
    const branch = {
        name: field.name,
        groupName: field.groupName,
        assignedDistributor: typeof field.assignedDistributor === 'object'
            ? field.assignedDistributor?.id
            : field.assignedDistributor,
        activity: field.activity,
    };

    let requestVO = {
        branch: branch,
        location: field.location,
        // Ключът е `company`, не `isCompany`: ClientVO го чете през setCompany(boolean), тоест
        // `isCompany` се игнорира тихо и полето остава false. Тогава BranchVO.setEntityPayment
        // приема обекта за частно лице и го записва на "стокова разписка", а типът на документа се
        // преписва оттам във всяка вдигната такса. Мобилното винаги е пращало `company`.
        client: { id: clientId, company: field.isCompany },
    };

    field.imgUrl && formData.append('img', field.imgUrl);

    formData.append(
        'requestVO',
        new Blob([JSON.stringify(requestVO)], {
            type: 'application/json',
        })
    );

    return interceptor.post(`/client-service/clients/branches/withImg${field.id ? '/' + field.id : ''}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
};

export const useGetInvoicableBranchOptions = (): Promise<Option[]> => {
    return interceptor.get('/client-service/clients/branches/selectItemsFilter', {
        params: {
            invoicable: true,
        },
        transformResponse: transformNoPagerResponse,
    });
};

export const useGetInvoices = (
    props: TableRequestParams,
    paid?: boolean,
    clientId?: number
): Promise<InvoiceData<Invoice[]>> => {
    const filter: InvoiceFilter | undefined = props.filter;
    const search = filter?.search ? filter.search : null;
    const branchId = filter?.id ? filter.id : null;
    const userId = filter?.userId? filter.userId : null;
    const type = filter?.type;
    const paidStatus = filter?.paid;
    const paidType = filter?.paidType;

    const paymentDateBefore = filter?.before;
    const paymentDayAfter = filter?.after;
    const isPaid = toPaidFlag(type) ?? toPaidFlag(paidStatus);

    // Бекендът чете полето devicePassportIds - под името passportIds то просто се подминаваше и филтърът
    // по устройства не стесняваше нищо. Оборотът винаги го е пращал правилно (users-hooks).
    const devicePassportIds: string | null =
        filter?.passportIds && filter.passportIds.length > 0 ? filter.passportIds.join(',') : null;
    const oilIds: string | null = filter?.oilIds && filter.oilIds.length > 0 ? filter.oilIds.join(',') : null;

    const sentByMail = filter?.sentByMail;

    return interceptor.get('/client-service/clients/invoicings', {
        params: {
            page: props['pager.page'] || undefined,
            itemsPerPage: props['pager.itemsPerPage'] || undefined,
            search,
            branchId,
            userId,
            paid: isPaid ?? paid,
            // Частично платената фактура стои в paid = false, тоест булевото поле не я отличава от
            // неплатената. Затова 'partial' пътува като отделен параметър, а paid остава непокътнат.
            paymentStatus: paidStatus === InvoicePaymentFilter.Partial ? InvoicePaymentFilter.Partial : null,
            paidType: paidType === 'all' ? null : paidType,
            documentType: type,
            paymentDateBefore,
            paymentDayAfter,
            devicePassportIds,
            oilIds,
            clientId,
            sentByMail: sentByMail ?? null,
            includeOpeningBalances: filter?.includeOpeningBalances ?? false,
            sortBy: filter?.sortBy || null,
            sortDirection: filter?.sortDirection || null,
        },
        transformResponse: transformInvoiceResponse,
    });
};

export const useGetSales = (
    props: TableRequestParams,
    paid?: boolean,
    clientId?: number
): Promise<InvoiceData<Invoice[]>> => {
    const filter: InvoiceFilter | undefined = props.filter;
    const search = filter?.search ? filter.search : null;
    const branchId = filter?.id ? filter.id : null;
    const userId = filter?.userId? filter.userId : null;
    const paidStatus = filter?.paid;
    const paidType = filter?.paidType;
    const type = filter?.type ?? null;

    const paymentDateBefore = filter?.before;
    const paymentDayAfter = filter?.after;
    const isPaid = toPaidFlag(type) ?? toPaidFlag(paidStatus);

    // Бекендът чете полето devicePassportIds - под името passportIds то просто се подминаваше и филтърът
    // по устройства не стесняваше нищо. Оборотът винаги го е пращал правилно (users-hooks).
    const devicePassportIds: string | null =
        filter?.passportIds && filter.passportIds.length > 0 ? filter.passportIds.join(',') : null;
    const oilIds: string | null = filter?.oilIds && filter.oilIds.length > 0 ? filter.oilIds.join(',') : null;
    const sentByMail = (filter as any)?.sentByMail;

    return interceptor.get('/client-service/sales/all', {
        params: {
            page: props['pager.page'] || undefined,
            itemsPerPage: props['pager.itemsPerPage'] || undefined,
            search,
            branchId,
            userId,
            paid: isPaid ?? paid,
            paidType: paidType === 'all' ? null : paidType,
            documentType: type,
            paymentDateBefore,
            paymentDayAfter,
            devicePassportIds,
            oilIds,
            clientId,
            sentByMail: sentByMail ?? null,
        },
        transformResponse: transformInvoiceResponse,
    });
};

/**
 * Задължение, вдигнато на ръка за един обект.
 *
 * <p>`officeGenerated` казва на backend-а, че задължението за този период идва от офиса, а не от
 * нощната задача. На него виси пазачът `hasOfficeInvoiceCovering`: издаде ли офисът фактурата,
 * посещението на дистрибутора не фактурира периода втори път. Без флага обект 9839 получи две
 * фактури за 05.09-04.10.2026 - едната от екрана, другата шест минути по-късно при обслужването.
 */
export const addInvoices = (
    id: number | null | undefined,
    invoice?: BranchPaymentInfo,
    filter?: Filter,
    officeGenerated = false
) => {
    resourceCache(useGetInvoices).clear();
    if (invoice) {
        invoice.fee *= 100;
        invoice.total *= 100;
        invoice.addition *= 100;
    }
    return (
        id &&
        interceptor.post(`/client-service/clients/invoicings/${id}`, {
            ...invoice,
            fromDate: filter?.after,
            duePaymentDate: filter?.before,
            officeGenerated,
        })
    );
};

/**
 * Протоколите за периода на всяка от подадените фактури - за списъка с отметки в диалога за
 * обединяване. Прозорецът се смята на сървъра.
 */
export const getInvoiceProtocols = (invoicingIds: number[]): Promise<InvoiceProtocols[]> => {
    return interceptor.get('/client-service/clients/invoicings/protocols', {
        params: { ids: invoicingIds.join(',') },
    });
};

/**
 * Всички редове на един документ. Обединената фактура има по един на обект или период, а диалогът за
 * промяна ги иска всичките - независимо какво е показано на страницата, откъдето е отворен.
 */
export const getDocumentInvoices = (documentId: number): Promise<Invoice[]> => {
    return interceptor.get(`/client-service/clients/invoicings/byDocument/${documentId}`);
};

/**
 * Последните протоколи на един обект - за списъка „добави протокол".
 *
 * Редовете в диалога носят само прозореца на своята фактура; оттук идва всичко на обекта, включително от
 * по-стар месец и след крайната дата. Тегли се чак при отваряне на списъка, за един обект.
 */
export const getBranchProtocols = (branchId: number, limit = 100): Promise<InvoiceProtocol[]> => {
    return interceptor.get('/client-service/clients/invoicings/protocols/branch', {
        params: { branchId, limit },
    });
};

/**
 * Запомня отметнатите протоколи по фактури: при изпращане по мейл тръгва точно това.
 *
 * Обектите с поне една отметка получават и настройката „прикачвай протоколи", без която прикачване няма
 * изобщо. Настройката само се включва - изключването е от картона на обекта.
 */
export const saveProtocolPicks = (
    picks: InvoiceProtocolPick[]
): Promise<{ changed: number; picked: number }> => {
    return interceptor.post('/client-service/clients/invoicings/protocols/attach', picks);
};

/**
 * Клиентите, които съвпадат с търсеното по име или ЕИК - за падащ списък.
 *
 * Отсяването е на сървъра, с таван от негова страна. Другият път - `/clients/short` - дърпа всичките
 * единайсет хиляди клиента с обектите им и оставя отсяването на браузъра; за търсачка това е излишно.
 */
export const searchClients = (search: string, itemsPerPage = 50): Promise<ClientSelectItem[]> => {
    return interceptor.get('/client-service/clients/selectItemsFilter', {
        params: { search, itemsPerPage },
    });
};

/**
 * Обектите на клиента за избрания месец: период, такса и вече издаденото за същия период.
 * `month` е `YYYY-MM`. Сумите остават в стотинки - делят се само при показване.
 */
export const getMonthlyInvoicePreview = (clientId: number, month: string): Promise<MonthlyInvoiceRow[]> => {
    return interceptor.get('/client-service/clients/invoicings/monthly/preview', {
        params: { clientId, month },
    });
};

/** Издава по едно задължение на избран обект. Периодът и сумата се смятат наново на сървъра. */
export const generateMonthlyInvoices = (
    clientId: number,
    month: string,
    branchIds: number[]
): Promise<MonthlyInvoiceGenerateResult> => {
    resourceCache(useGetInvoices).clear();
    return interceptor.post('/client-service/clients/invoicings/monthly/generate', branchIds, {
        params: { clientId, month },
    });
};

export const useEditInvoices = (fields: EditInvoice): Promise<{ id: number }> => {
    fields.total *= 100;
    resourceCache(useGetInvoices).clear();
    const taskType = fields.taskType;

    return interceptor.put(
        `/client-service/clients/invoicings/fees/${fields.id}`,
        { ...fields, total: fields.total.toFixed(2) },
        { params: { taskType } }
    );
};

export const useEditForSaleInvoice = (fields: EditInvoice): Promise<{ id: number }> => {
    fields.total *= 100;
    resourceCache(useGetSales).clear();

    return interceptor.put(
        `/client-service/sales/draft`,
        { saleId: fields.id, discount: fields.discount, total: fields.total }
    );
};

/**
 * Потвърждава продажба по банка: фактурата получава номер от брояча на офиса, същия, който ползват и
 * фактурите по наем. Мобилното нарочно не дава номер по този път - там няма фискален печат.
 */
export const confirmSaleInvoice = (saleId: number, comment?: string): Promise<{ id: number }> => {
    resourceCache(useGetSales).clear();
    return interceptor.post(
        `/client-service/sales/${saleId}/confirm` + (comment ? `?comment=${encodeURIComponent(comment)}` : ''),
        undefined,
        { skipErrorInterceptor: true }
    );
};

// Отделен ендпойнт от emailInvoice: продажбата няма обект, така че получателят е имейлът на клиента, а
// не на обекта. Общият път търси id-то в таблицата на наемите и не намира продажбата.
export const emailSaleInvoice = (saleId: number, resend = false): Promise<{ id: number }> => {
    return interceptor.post(
        `/client-service/sales/${saleId}/email` + (resend ? '?resend=true' : ''),
        undefined,
        { skipErrorInterceptor: true }
    );
};

/**
 * Групово изпращане на фактури по продажби. Бекендът групира по получател - клиент с няколко фактури
 * получава едно писмо с всичките. Връща id-тата на изпратените; непотвърдените се пропускат.
 */
export const emailSaleInvoices = (ids: number[]): Promise<number[]> => {
    const params = ids.join(',');
    return interceptor.post(`/client-service/sales/email/list?ids=${params}`, undefined, {
        skipErrorInterceptor: true,
    });
};

export const getSaleEmailRecipient = (saleId: number): Promise<{ email: string; emails?: string[] }> => {
    return interceptor.get(`/client-service/sales/${saleId}/email/recipient`);
};

export const deleteDraftInvoiceSale = (id: number | undefined, comment: string): Promise<void> => {
    resourceCache(useGetSales).clear();
    return interceptor.delete(`/client-service/sales/delete?id=${id}&comment=${comment}`);
};

// skipErrorInterceptor: глобалният интерцептор показва само общия текст за кода на грешката
// ("Грешно въведени данни") и хвърля същинското съобщение в конзолата. Тези извиквания го поемат сами -
// виж showBackendError.
export const emailInvoice = (id: number): Promise<{ id: number }> => {
    return interceptor.post(`/client-service/clients/invoicings/email/${id}`, undefined, {
        skipErrorInterceptor: true,
    });
};

export const emailInvoices = (ids: number[]): Promise<{ ids: number[] }> => {
    const params = ids.join(',');
    return interceptor.post(`/client-service/clients/invoicings/email/list?ids=${params}`, undefined, {
        skipErrorInterceptor: true,
    });
};

// Отделен ендпойнт от emailInvoice: обикновеният отказва фактура, която вече е изпратена. Този я праща
// пак, но стои зад потвърждение в интерфейса.
export const resendInvoiceEmail = (id: number): Promise<{ id: number }> => {
    return interceptor.post(`/client-service/clients/invoicings/email/${id}/resend`, undefined, {
        skipErrorInterceptor: true,
    });
};

// Обектът на фактурата. Идва от самата фактура, а не от документа към нея - document.branchId се
// попълва само от част от пътищата, които създават документ.
export const getInvoiceBranchId = (id: number): Promise<{ branchId: number | null }> => {
    return interceptor.get(`/client-service/clients/invoicings/${id}/branch`);
};

export interface EmailRecipientItem {
    email: string;
    branches?: string[];
}

// Текущият адрес на клиента, показва се в потвърждението. Не се пази на кой адрес е ходило миналия път -
// само датата, в emailSentDate.
export const getInvoiceEmailRecipient = (id: number): Promise<{
    email: string;
    emails?: string[];
    recipients?: EmailRecipientItem[];
}> => {
    return interceptor.get(`/client-service/clients/invoicings/email/${id}/recipient`);
};

export const deleteInvoices = (fields?: Invoice, comment?: string) => {
    return interceptor.delete(`/client-service/clients/invoicings/${fields?.id}?comment=` + comment);
};

export const getInvoiceClientId = (invoiceId: number, sale = false): Promise<number> =>
    interceptor.get(`/client-service/clients/invoicings/${invoiceId}/client-id`, { params: { sale } });

export const addNumberToInvoice = (invoiceId?: number, comment?: string) => {
    // Без коментар заявката тръгваше с буквалното `?comment=undefined`.
    const query = comment ? '?comment=' + encodeURIComponent(comment) : '';
    return interceptor.post(
        '/client-service/clients/invoicings/addNumberByInvoiceId/' + invoiceId + query
    );
};

export const saveInvoicesToDocument = (document?: DocumentView) => {
    return interceptor.post('/client-service/clients/invoicings/saveInvoicesToDocument', {
        ...document,
    });
};

/**
 * Документът на фактурата.
 *
 * Празно id не тръгва към сървъра. Модалът за редакция може да се преиздаде без избран ред - докато се
 * затваря, или когато плащането затвори фактурата и тя излезе от филтъра "неплатени" - и тогава
 * заявката тръгваше буквално като /getDocument/undefined, което бекендът отказва с 400 и пълни лога.
 * Празният документ носи invoices, защото извикващият чете дължината му.
 */
export const useGetDocument = (documentId?: number): Promise<DocumentView> => {
    if (documentId === undefined || documentId === null) {
        return Promise.resolve({ invoices: [] } as unknown as DocumentView);
    }
    return interceptor.get('/client-service/clients/invoicings/getDocument/' + documentId);
};

export const useDeleteProtocol = (fields?: HandoverProtocol, comment?: string) => {
    return interceptor.delete(`/warehouse-service/docs/deleteHandover/${fields?.id}?comment=` + comment);
};

export const useGetNewRequestClientDevices = (id: number): Promise<TableData<BranchDevice[]>> => {
    return interceptor.get(`client-service/clients/devices?branchId=${id}&view=REQUESTVIEW`);
};

export interface InventoryRevisionRow {
    [key: string]: unknown;
}

export interface InventoryRevisionGroups {
    [type: string]: InventoryRevisionRow[];
}

export type InventoryRevisionCollection = InventoryRevisionRow[] | InventoryRevisionGroups;

export interface InventoryRevisionResponse {
    inventory?: InventoryRevisionCollection;
    revision?: InventoryRevisionCollection;
    revizion?: InventoryRevisionCollection;
    available?: InventoryRevisionRow[];
    availableItems?: InventoryRevisionRow[];
    availableQuantities?: InventoryRevisionRow[];
    real?: InventoryRevisionRow[];
    realQuantities?: InventoryRevisionRow[];
    actual?: InventoryRevisionRow[];
    actualQuantities?: InventoryRevisionRow[];
    submitted?: InventoryRevisionRow[];
    submittedItems?: InventoryRevisionRow[];
    submittedQuantities?: InventoryRevisionRow[];
    listed?: InventoryRevisionRow[];
    listedQuantities?: InventoryRevisionRow[];
    counted?: InventoryRevisionRow[];
    countedQuantities?: InventoryRevisionRow[];
    [key: string]: unknown;
}

export interface InventoryRevisionDifferenceRecord {
    id?: number;
    action?: string;
    createdAt?: string;
    createdDate?: string;
    date?: string;
    revisionDate?: string;
    performedByUserId?: number;
    performedByUserName?: string;
    readAt?: string;
    readByUserId?: number;
    readByUserName?: string;
    differences?: InventoryRevisionRow[];
    items?: InventoryRevisionRow[];
    [key: string]: unknown;
}

interface PendingInventoryRevisionDistributorsResponse {
    hasPending?: boolean;
    distributorIds?: number[];
}

export const inventoryRevisionObservationsReadEvent = 'inventory-revision-observations-read';

export const getInventoryRevision = (distributorId: number): Promise<InventoryRevisionResponse> => {
    return interceptor.get('/warehouse-service/api/inventory/revision/summary', {
        params: {
            userId: distributorId,
        },
    });
};

export const getInventoryRevisionDistributors = (): Promise<TableData<User[]>> => {
    return interceptor.get('/warehouse-service/api/inventory/revision/distributors', {
        transformResponse: (response: string): TableData<User[]> => {
            const data = response ? JSON.parse(response) : [];
            const items = Array.isArray(data) ? data : data.items ?? [];

            return {
                items,
                pager: data.pager ?? {},
            };
        },
    });
};

const inventoryRevisionSubmittedCollectionKeys = [
    'revision',
    'revizion',
    'submitted',
    'submittedItems',
    'submittedQuantities',
    'listed',
    'listedQuantities',
    'counted',
    'countedQuantities',
];

const inventoryRevisionAvailableCollectionKeys = [
    'inventory',
    'available',
    'availableItems',
    'availableQuantities',
    'real',
    'realQuantities',
    'actual',
    'actualQuantities',
];

const normalizeInventoryRevisionCollection = (value: unknown): InventoryRevisionRow[] => {
    if (Array.isArray(value)) return value as InventoryRevisionRow[];
    if (!value || typeof value !== 'object') return [];

    return Object.entries(value as Record<string, unknown>).reduce<InventoryRevisionRow[]>((rows, [type, items]) => {
        if (!Array.isArray(items)) return rows;

        return [
            ...rows,
            ...items.map((item) => ({
                type,
                ...(item as InventoryRevisionRow),
            })),
        ];
    }, []);
};

const getInventoryRevisionFirstCollection = (response: InventoryRevisionResponse | undefined, keys: string[]) => {
    if (!response) return [];

    for (const key of keys) {
        const collection = normalizeInventoryRevisionCollection(response[key]);
        if (collection.length > 0) return collection;
    }

    return [];
};

const splitInventoryRevisionResponse = (response?: InventoryRevisionResponse) => {
    return {
        availableItems: getInventoryRevisionFirstCollection(response, inventoryRevisionAvailableCollectionKeys),
        submittedItems: getInventoryRevisionFirstCollection(response, inventoryRevisionSubmittedCollectionKeys),
    };
};

const formatInventoryRevisionCellValue = (value: unknown) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'object') return JSON.stringify(value);

    return String(value);
};

const getInventoryRevisionItemCountValue = (item: InventoryRevisionRow) => {
    const countFields = [
        item.count,
        item.itemCount,
        item.quantity,
        item.countQuantity,
        item.totalCount,
        item.itemsCount,
    ];
    const nonZeroCount = countFields.find((value) => {
        const quantity = Number(value);

        return !Number.isNaN(quantity) && quantity !== 0;
    });

    return nonZeroCount ?? item.count;
};

const getInventoryRevisionItemIdentityKey = (item: InventoryRevisionRow) => {
    const type = formatInventoryRevisionCellValue(item.type);
    const idOfPassport = item.idOfPassport;
    if (idOfPassport !== null && idOfPassport !== undefined) return `${type}-${idOfPassport}`;

    return `${type}-${formatInventoryRevisionCellValue(item.passport ?? item.name)}`;
};

const isSprayWeightInventoryRevisionItem = (item: InventoryRevisionRow) => {
    const trackingType = formatInventoryRevisionCellValue(
        item.trackingType ?? item.consumableTrackingType ?? item.passportTrackingType
    ).toUpperCase();

    return trackingType === 'SPRAY_WEIGHT' || trackingType === 'SPRAY_WIGHT' || trackingType === 'SPRAY_WEGHT';
};

const getSprayWeightInventoryRevisionItemKeys = (items: InventoryRevisionRow[]) => {
    return items.reduce<Set<string>>((keys, item) => {
        if (isSprayWeightInventoryRevisionItem(item)) keys.add(getInventoryRevisionItemIdentityKey(item));

        return keys;
    }, new Set());
};

const getInventoryRevisionItemQuantityInfo = (
    item: InventoryRevisionRow,
    sprayWeightItemKeys: Set<string> = new Set()
) => {
    if (isSprayWeightInventoryRevisionItem(item) || sprayWeightItemKeys.has(getInventoryRevisionItemIdentityKey(item))) {
        return { value: getInventoryRevisionItemCountValue(item), unit: 'pcs' };
    }

    const explicitUnit = formatInventoryRevisionCellValue(item.quantityUnit);
    if (explicitUnit) return { value: getInventoryRevisionItemCountValue(item), unit: explicitUnit };

    const isOil = String(item.type).toLowerCase() === 'oil';
    const isEmpty = String(item.state).toLowerCase() === 'empty';
    const hasAvailableAmount = item.availableAmount !== null && item.availableAmount !== undefined && item.availableAmount !== '';

    if (isOil && !isEmpty && hasAvailableAmount) {
        return { value: item.availableAmount, unit: 'ml' };
    }

    return { value: item.count, unit: 'pcs' };
};

const getInventoryRevisionItemQuantity = (
    item: InventoryRevisionRow,
    sprayWeightItemKeys: Set<string> = new Set()
) => {
    const { value } = getInventoryRevisionItemQuantityInfo(item, sprayWeightItemKeys);
    const quantity = Number(value);

    return Number.isNaN(quantity) ? 0 : quantity;
};

const isLiquidOilPcsInventoryRevisionItem = (item: InventoryRevisionRow) => {
    const type = formatInventoryRevisionCellValue(item.type).toUpperCase();
    if (type !== 'OIL') return false;
    if (isSprayWeightInventoryRevisionItem(item)) return false;

    return getInventoryRevisionItemQuantityInfo(item).unit === 'pcs';
};

const filterInventoryRevisionOverwriteItems = (items: InventoryRevisionRow[]) => {
    return items.filter((item) => !isLiquidOilPcsInventoryRevisionItem(item));
};

interface AggregatedInventoryRevisionItem {
    idOfPassport?: unknown;
    name: string;
    quantity: number;
    type?: unknown;
    unit: string;
}

const getInventoryRevisionItemKey = (
    item: InventoryRevisionRow,
    sprayWeightItemKeys: Set<string> = new Set()
) => {
    const type = formatInventoryRevisionCellValue(item.type);
    const { unit } = getInventoryRevisionItemQuantityInfo(item, sprayWeightItemKeys);
    const idOfPassport = item.idOfPassport;
    if (idOfPassport !== null && idOfPassport !== undefined) return `${type}-${unit}-${idOfPassport}`;

    return `${type}-${unit}-${formatInventoryRevisionCellValue(item.passport ?? item.name)}`;
};

const aggregateInventoryRevisionItems = (
    items: InventoryRevisionRow[],
    sprayWeightItemKeys: Set<string>
) => {
    return items.reduce<Record<string, AggregatedInventoryRevisionItem>>((aggregated, item) => {
        const key = getInventoryRevisionItemKey(item, sprayWeightItemKeys);
        const name = formatInventoryRevisionCellValue(item.passport ?? item.name);
        const { unit } = getInventoryRevisionItemQuantityInfo(item, sprayWeightItemKeys);
        const quantity = getInventoryRevisionItemQuantity(item, sprayWeightItemKeys);

        if (!aggregated[key]) {
            aggregated[key] = {
                idOfPassport: item.idOfPassport,
                name,
                quantity: 0,
                type: item.type,
                unit,
            };
        }

        aggregated[key].quantity += quantity;
        return aggregated;
    }, {});
};

export const hasPendingInventoryRevisionDifferences = (response?: InventoryRevisionResponse) => {
    const { availableItems, submittedItems } = splitInventoryRevisionResponse(response);
    if (submittedItems.length === 0) return false;

    const filteredAvailableItems = filterInventoryRevisionOverwriteItems(availableItems);
    const filteredSubmittedItems = filterInventoryRevisionOverwriteItems(submittedItems);
    const sprayWeightItemKeys = getSprayWeightInventoryRevisionItemKeys([
        ...filteredAvailableItems,
        ...filteredSubmittedItems,
    ]);
    const availableByKey = aggregateInventoryRevisionItems(filteredAvailableItems, sprayWeightItemKeys);
    const submittedByKey = aggregateInventoryRevisionItems(filteredSubmittedItems, sprayWeightItemKeys);
    const allKeys = Array.from(new Set([...Object.keys(availableByKey), ...Object.keys(submittedByKey)]));

    return allKeys.some((key) => {
        const availableQuantity = availableByKey[key]?.quantity ?? 0;
        const submittedQuantity = submittedByKey[key]?.quantity ?? 0;

        return submittedQuantity - availableQuantity !== 0;
    });
};

const pendingInventoryRevisionCacheDurationMs = 120000;
let pendingInventoryRevisionDistributorIdsCache: {
    fetchedAt: number;
    distributorIds: number[];
} | undefined;
let pendingInventoryRevisionDistributorIdsPromise: Promise<number[]> | undefined;

export const getPendingInventoryRevisionDistributorIds = async (forceRefresh = false): Promise<number[]> => {
    const now = Date.now();
    if (!forceRefresh && pendingInventoryRevisionDistributorIdsCache &&
        now - pendingInventoryRevisionDistributorIdsCache.fetchedAt < pendingInventoryRevisionCacheDurationMs) {
        return pendingInventoryRevisionDistributorIdsCache.distributorIds;
    }

    if (pendingInventoryRevisionDistributorIdsPromise) {
        return pendingInventoryRevisionDistributorIdsPromise;
    }

    pendingInventoryRevisionDistributorIdsPromise = (async () => {
        const response = await interceptor.get<PendingInventoryRevisionDistributorsResponse, PendingInventoryRevisionDistributorsResponse>(
            '/warehouse-service/api/inventory/revision/pending-distributors'
        );
        const activeDistributorIds = (response.distributorIds ?? [])
            .filter((distributorId): distributorId is number => typeof distributorId === 'number');

        pendingInventoryRevisionDistributorIdsCache = {
            fetchedAt: Date.now(),
            distributorIds: activeDistributorIds,
        };

        return activeDistributorIds;
    })().finally(() => {
        pendingInventoryRevisionDistributorIdsPromise = undefined;
    });

    return pendingInventoryRevisionDistributorIdsPromise;
};

export const getUnreadInventoryRevisionObservationDistributorIds = async (): Promise<number[]> => {
    const response = await interceptor.get<PendingInventoryRevisionDistributorsResponse, PendingInventoryRevisionDistributorsResponse>(
        '/warehouse-service/api/inventory/revision/unread-observations/distributors'
    );

    return (response.distributorIds ?? [])
        .filter((distributorId): distributorId is number => typeof distributorId === 'number');
};

export const getInventoryRevisionDifferences = (
    distributorId: number
): Promise<InventoryRevisionDifferenceRecord[]> => {
    return interceptor.get('/warehouse-service/api/inventory/revision/differences', {
        params: {
            userId: distributorId,
        },
        transformResponse: (response: string): InventoryRevisionDifferenceRecord[] => {
            const data = response ? JSON.parse(response) : [];
            return Array.isArray(data) ? data : data.items ?? [];
        },
    });
};

export const markInventoryRevisionHistoryRead = (
    historyId: number
): Promise<InventoryRevisionDifferenceRecord> => {
    return interceptor.post(`/warehouse-service/api/inventory/revision/history/${historyId}/read`);
};

interface RevisionOverwriteReceiptItem {
    receivingUserId?: number;
    state?: boolean | null;
    handoutStatus?: string;
    status?: boolean | null;
}

interface RevisionOverwriteReceiptGroup {
    receivingUserId?: number;
    items?: RevisionOverwriteReceiptItem[];
}

interface RevisionOverwriteReceiptDateGroup {
    distributors?: RevisionOverwriteReceiptGroup[];
    items?: RevisionOverwriteReceiptItem[];
}

interface RevisionOverwriteReturnedGroup {
    userId?: number;
    items?: unknown[];
    itemsCount?: number;
    totalCount?: number;
}

export interface InventoryRevisionOverwriteBlockers {
    hasPendingInventoryReceipts: boolean;
    hasReturnedItemsToWarehouse: boolean;
    pendingInventoryReceiptsCount: number;
    returnedItemsToWarehouseCount: number;
}

const normalizeTableItems = <T extends unknown>(response: string): T[] => {
    const data = response ? JSON.parse(response) : [];
    return Array.isArray(data) ? data : data.items ?? [];
};

const isAwaitingReceipt = (receipt: RevisionOverwriteReceiptItem) => {
    if (receipt.handoutStatus === 'AWAITING') return true;
    if (receipt.status === null) return true;

    return receipt.state === null;
};

const hasAwaitingReceiptForDistributor = (
    receiptDateGroups: RevisionOverwriteReceiptDateGroup[],
    distributorId: number
) => receiptDateGroups.some((dateGroup) =>
    (dateGroup.distributors ?? []).some((distributorGroup) =>
        distributorGroup.receivingUserId === distributorId &&
        (distributorGroup.items ?? []).some(isAwaitingReceipt)
    ) ||
    (dateGroup.items ?? []).some((receipt) =>
        receipt.receivingUserId === distributorId && isAwaitingReceipt(receipt)
    )
);

const getAwaitingReceiptCountForDistributor = (
    receiptDateGroups: RevisionOverwriteReceiptDateGroup[],
    distributorId: number
) => receiptDateGroups.reduce((totalCount, dateGroup) => {
    const groupedReceiptCount = (dateGroup.distributors ?? []).reduce((groupTotal, distributorGroup) => {
        if (distributorGroup.receivingUserId !== distributorId) return groupTotal;

        return groupTotal + (distributorGroup.items ?? []).filter(isAwaitingReceipt).length;
    }, 0);

    const flatReceiptCount = (dateGroup.items ?? []).filter((receipt) =>
        receipt.receivingUserId === distributorId && isAwaitingReceipt(receipt)
    ).length;

    return totalCount + groupedReceiptCount + flatReceiptCount;
}, 0);

const hasReturnedItemsForDistributor = (
    returnedGroups: RevisionOverwriteReturnedGroup[],
    distributorId: number
) => returnedGroups.some((group) =>
    group.userId === distributorId &&
    ((group.items?.length ?? 0) > 0 || (group.itemsCount ?? 0) > 0 || (group.totalCount ?? 0) > 0)
);

const getReturnedItemsCountForDistributor = (
    returnedGroups: RevisionOverwriteReturnedGroup[],
    distributorId: number
) => returnedGroups.reduce((totalCount, group) => {
    if (group.userId !== distributorId) return totalCount;

    return totalCount + (group.items?.length ?? group.itemsCount ?? group.totalCount ?? 0);
}, 0);

export const getInventoryRevisionOverwriteBlockers = async (
    distributorId: number
): Promise<InventoryRevisionOverwriteBlockers> => {
    const [receiptDateGroups, returnedGroups] = await Promise.all([
        interceptor.get<any, RevisionOverwriteReceiptDateGroup[]>('/warehouse-service/api/inventory/getItemReceipts', {
            params: {
                userId: distributorId,
                page: 1,
                itemsPerPage: 100000,
            },
            transformResponse: (response: string): RevisionOverwriteReceiptDateGroup[] =>
                normalizeTableItems<RevisionOverwriteReceiptDateGroup>(response),
        }),
        interceptor.get<any, RevisionOverwriteReturnedGroup[]>('/warehouse-service/api/inventory/getAllReturnedItems', {
            params: {
                page: 1,
                itemsPerPage: 100000,
            },
            transformResponse: (response: string): RevisionOverwriteReturnedGroup[] =>
                normalizeTableItems<RevisionOverwriteReturnedGroup>(response),
        }),
    ]);

    const pendingInventoryReceiptsCount = getAwaitingReceiptCountForDistributor(receiptDateGroups, distributorId);
    const returnedItemsToWarehouseCount = getReturnedItemsCountForDistributor(returnedGroups, distributorId);

    return {
        hasPendingInventoryReceipts: hasAwaitingReceiptForDistributor(receiptDateGroups, distributorId),
        hasReturnedItemsToWarehouse: hasReturnedItemsForDistributor(returnedGroups, distributorId),
        pendingInventoryReceiptsCount,
        returnedItemsToWarehouseCount,
    };
};

export const overwriteInventoryRevision = (
    distributorId: number,
    differences: InventoryRevisionRow[]
): Promise<InventoryRevisionResponse> => {
    return interceptor.post(
        '/warehouse-service/api/inventory/revision/overwrite',
        { differences },
        {
            params: {
                userId: distributorId,
            },
        }
    );
};

export const keepInventoryRevision = (
    distributorId: number,
    differences: InventoryRevisionRow[]
): Promise<InventoryRevisionResponse> => {
    return interceptor.post(
        '/warehouse-service/api/inventory/revision/keep',
        { differences },
        {
            params: {
                userId: distributorId,
            },
        }
    );
};
export const addNewRequestClientDevices = (
    fields: BranchDevice,
    branchId: number,
    installNow: boolean
): Promise<BranchDevice> => {
    const { materials: deviceMaterials, ...branchDevice } = fields;
    const materials = deviceMaterials?.map(({ passportId, itemCount }) => ({
        itemCount: itemCount,
        passportId: passportId,
    }));
    return interceptor.post(`client-service/clients/devices`, {
        branchId,
        ...branchDevice,
        materials,
        installNow,
    });
};
export const editNewRequestClientDevices = (
    fields: BranchDevice,
    deviceId: number,
    branchId: number
): Promise<CreateEntitieResponse> => {
    const { materials: deviceMaterials, ...branchDevice } = fields;
    // const materials = deviceMaterials.map(({ deviceId, itemId, count }) => ({ deviceId, itemCount: count, passportId: itemId }));
    return interceptor.post(`client-service/clients/devices`, {
        deviceId,
        branchId,
        // materials,
        ...branchDevice,
    });
};
export const deleteNewRequestClientDevices = ({
    deviceId,
    employeeId,
    state,
    reason,
}: {
    deviceId: number;
    employeeId: string;
    state: string;
    reason?: string;
}) => {
    return interceptor.delete(`client-service/clients/devices/${deviceId}`, {
        params: {
            employeeId,
            state,
            reason,
        },
    });
};

export const useGetAllBranchMaterials = (branchId: number): Promise<MaterialBranch[]> => {
    return interceptor.get(`client-service/clients/materials/byBranch?branchId=${branchId}`);
};
export const useGetBranchPaymentInfo = (id: number | undefined): Promise<BranchPaymentInfo> => {
    return interceptor.get(`client-service/clients/branches/paymentInfo/${id}`, {
        transformResponse: transformFeeValue,
    });
};
export const getBranchPaymentInfo = (id: number): Promise<BranchPaymentInfo> => {
    return interceptor.get(`client-service/clients/branches/paymentInfo/${id}`, {
        transformResponse: transformFeeValue,
    });
};

export const editBranchPaymentInfo = (fields: BranchPaymentInfo, branchId: number, reason?: string) => {
    fields.fee *= 100;
    fields.total *= 100;
    fields.addition *= 100;
    return interceptor.put(
        `client-service/clients/branches/paymentInfo/${branchId}`,
        {
            ...fields,
        },
        { params: { reason } }
    );
};

export const transformFeeValue = (response: string) => {
    const data = JSON.parse(response);
    data.fee /= 100;
    data.total /= 100;
    data.addition /= 100;
    return data;
};

export const deleteMaterialFromBranch = (materialId: number, count: number, distributorId: string, state: string) => {
    return interceptor.delete(
        `client-service/clients/materials/${materialId}?count=${count}&state=${state}&employeeId=${distributorId}`
    );
};

export const useGetBranchPieChartData = (filter: Filter): Promise<BranchPieChartData> => {
    return interceptor.get(`client-service/api/user/turnover/report/count/branches`, {
        params: { createdBefore: filter.before, createdAfter: filter.after },
    });
};
export const useGetDevicePieChartData = (filter: Filter): Promise<DevicePieChartData> => {
    return interceptor.get(`client-service/api/user/turnover/report/count/devices`, {
        params: { createdBefore: filter.before, createdAfter: filter.after },
    });
};
export const useGetDistributorBranchBarChartData = (filter: Filter): Promise<DistributorBranchChartData[]> => {
    return interceptor.get(`client-service/api/user/turnover/report/count/branches/byDistributor`, {
        params: { createdBefore: filter.before, createdAfter: filter.after, distributorIds: filter.tags.join(',') },
    });
};
export const useGetDistributorDeviceBarChartData = (filter: Filter): Promise<DistributorDeviceChartData[]> => {
    return interceptor.get(`client-service/api/user/turnover/report/count/devices/byDistributor`, {
        params: { createdBefore: filter.before, createdAfter: filter.after, distributorIds: filter.tags.join(',') },
    });
};

export const deleteTurnover = (fields: Turnover) => {
    return interceptor.delete('/client-service/api/user/turnover/delete/' + fields.id);
};

export const getInvoicesPDF = (invoiceIds: number[]): Promise<Blob> => {
    return interceptor.post(
        'client-service/docs/invoices',
        invoiceIds,
        {
            responseType: 'blob',
            transformResponse: (data) => {
                return new Blob([data], { type: 'application/pdf' });
            },
        },
    );
};

export const getDocumentsPDF = (documents: InvoicelessDocumentView[]): Promise<Blob> => {
    return interceptor.post(
        'client-service/docs/documents',
        documents,
        {
            responseType: 'blob',
            transformResponse: (data) => {
                return new Blob([data], { type: 'application/pdf' });
            },
        },
    );
};

export const getInvoicePDF = (documentId?: number, userid?: number): Promise<Blob> => {
    return interceptor.post(
        'client-service/docs/invoice?documentId=' + documentId + '&userId=' + userid,
        { documentId },
        {
            responseType: 'blob',
            transformResponse: (data) => {
                return new Blob([data], { type: 'application/pdf' });
            },
        }
    );
};
export const getInvoicePDFWithFormValues = (
    invoiceData: InvoicePreview,
    documentId?: number,
): Promise<Blob> => {
    return interceptor.post(
        'client-service/docs/invoiceWithFormValues?documentId=' + documentId,
        invoiceData,
        {
            responseType: 'blob',
            transformResponse: (data) => {
                return new Blob([data], { type: 'application/pdf' });
            },
        }
    );
};
export const getCreditNotePDF = (
    invoiceData: InvoicePreview | null,
    documentId?: number,
    userid?: number
): Promise<Blob> => {
    return interceptor.post(
        'client-service/docs/creditNote?documentId=' + documentId + '&userId=' + userid,
        invoiceData ? invoiceData : { documentId },
        {
            responseType: 'blob',
            transformResponse: (data) => {
                return new Blob([data], { type: 'application/pdf' });
            },
        }
    );
};
export const getInvoicePreview = (
    documentId?: number,
    creditNote?: boolean,
): Promise<InvoicePreview> => {
    return interceptor.post(
        'client-service/docs/generateInvoicePreview?documentId=' +
        documentId +
        '&creditNote=' +
        creditNote,
        { documentId }
    );
};

export const deleteBranch = (branchId: number, reason?: string) => {
    return interceptor.delete(`client-service/clients/branches/${branchId}`, {
        params: {
            reason,
        },
    });
};

export const updateMaintenanceDate = (branchId: number, newDate: string) => {
    return interceptor.post(`timer-service/api/status/updateMaintenanceDate?branchId=${branchId}&newDate=${newDate}`);
};

export const UseExportBranches = (branches: BranchListItem[], userId: number) => {
    const EXPORT_BRANCHES_URL = `client-service/clients/branches/changeAssignedDistributor`;
    return interceptor.put(`${EXPORT_BRANCHES_URL}`, branches, {
        headers: { user: userId },
    });
};

export const getDocumentsXML = (documents: number[]): Promise<Blob> => {
    return interceptor.post(
        'client-service/docs/xml',
        documents,
        {
            responseType: 'blob',
            transformResponse: (data) => {
                return new Blob([data], { type: 'application/xml' });
            },
        },
    );
};

export interface MarkAsPaidRequest {
    id: number;
    invoiceNumber: number;
    documentNumber?: number;
    paymentTimestamp?: string;
    isSale: boolean;
}

export const markInvoiceAsPaid = (request: MarkAsPaidRequest): Promise<{ id: number }> => {
    resourceCache(useGetInvoices).clear();
    resourceCache(useGetSales).clear();
    return interceptor.put('/client-service/sales/admin/markPaid', request);
};

export const sendArchiveEmailRange = (from: string, toDate: string, toEmail: string): Promise<void> => {
    return interceptor.post('client-service/internal/archive/send-range', null, {
        params: { from, toDate, to:toEmail },
    });
};

/** Затваря неплатена банкова фактура срещу кредитното известие по нея - без постъпление в оборота. */
export const offsetWithCreditNote = (documentId: number, comment?: string): Promise<CreditNoteOffset> => {
    resourceCache(useGetInvoices).clear();
    return interceptor.post(`/client-service/clients/invoicings/documents/${documentId}/credit-note-offset`, { comment });
};

export const getInvoicePayments = (invoicingId: number): Promise<InvoicePayments> => {
    return interceptor.get(`/client-service/clients/invoicings/${invoicingId}/payments`);
};

export const addInvoicePayment = (invoicingId: number, payment: InvoicePaymentRequest): Promise<void> => {
    return interceptor.post(`/client-service/clients/invoicings/${invoicingId}/payments`, payment);
};

export const deleteInvoicePayment = (paymentId: number, comment: string): Promise<void> => {
    return interceptor.delete(`/client-service/clients/invoicings/payments/${paymentId}`, {
        params: { comment },
    });
};

export const checkMergeableSiblings = (invoicingId: number): Promise<MergeableSibling[]> => {
    return interceptor.get(`/client-service/clients/invoicings/mergeableSiblings/${invoicingId}`);
};

export const mergeAndConfirmInvoices = (invoicingIds: number[], corrections?: InvoiceCorrectionInput[], breakdowns?: import('../models/invoice-corrections').InvoiceBreakdownInput[]): Promise<void> => {
    if (corrections !== undefined || breakdowns?.length) {
        return interceptor.post('/client-service/clients/invoicings/confirmWithCorrections', { invoicingIds, corrections: corrections ?? [], breakdowns });
    }

    return interceptor.post('/client-service/clients/invoicings/mergeAndConfirm', invoicingIds);
};

export const getDevicesCountByStatus = (params?: any): Promise<{ content: Device[], totalPages: number, totalElements: number }> => {
    return interceptor.get('/client-service/clients/devices/countByStatus', {
        params: {
            ...params,
        },
    });
};

export const getDevicesCountByStatusSummary = (params?: any): Promise<any> => {
    return interceptor.get('/client-service/clients/devices/countByStatus/summary', {
        params: {
            ...params,
        },
    });
};

export const getDevicesCountByStatusPrices = (params?: any): Promise<any> => {
    return interceptor.get('/client-service/clients/devices/countByStatus/prices', {
        params: {
            ...params,
        },
    });
};

export const exportDevicesCountByStatusPrices = (params?: any): Promise<Blob> => {
    return interceptor.get('/client-service/clients/devices/countByStatus/prices/export', {
        params: {
            ...params,
        },
        responseType: 'blob',
        transformResponse: (data) => {
            return new Blob([data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
        },
    });
};

export const exportDevicesCountByStatus = (params?: any): Promise<Blob> => {
    return interceptor.get('/client-service/clients/devices/countByStatus/export', {
        params: {
            ...params,
        },
        responseType: 'blob',
        transformResponse: (data) => {
            return new Blob([data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
        },
    });
};
